// Shared save-project flow used by the toolbar Save button and by the
// "ensure saved before render" gate in front of the Export dialog.

import type {AppDispatch} from '../../../common/store'
import type {DeviceConfig} from '../../../common/components/types'
import {store} from '../../../common/store'
import {
  setBgImageUploaded,
  setScreenImageUploaded,
  setCustomDecalUploaded,
  setSprite2DUploaded,
  setSprite3DUploaded,
} from '../../../common/components/store/scenesSlice'
import {createMyProject, updateMyProject} from '../../../common/api/generated/endpoints/my-projects/my-projects'
import {serializeConfig} from './serializeConfig'
import {setProjectName as setGlobalProjectName} from '../../../common/components/store/projectNameStore'
import {setProjectId as setGlobalProjectId} from '../../../common/components/store/projectIdStore'
import {setSavedSnapshot} from '../../../common/components/store/savedSnapshotStore'
import {stableStringify} from '../../../common/utils/stableStringify'
import {uploadAsset} from '../../../common/api/uploadAsset'

interface SaveProjectArgs {
  dispatch: AppDispatch
  deviceConfig: DeviceConfig
  /** Existing project id to update; undefined or 'new' triggers a create. */
  existingId: string | undefined
  /** Required when creating (no existingId). Ignored when updating. */
  name?: string
  /** Captured at create time to track fork lineage. Ignored when updating. */
  forkedFromSlug?: string
}

interface SaveProjectResult {
  id: string
}

// Guards against two concurrent first-saves creating duplicate projects. The toolbar
// Save button and the "save before Export" gate are independent callers; if both fire
// before either has an id, they would both POST. While a create is in flight, later
// creates await it and persist their state as an update instead. Mirrors the
// clientRenderId idempotency used for renders.
let inFlightCreate: Promise<string> | null = null

/**
 * Persist the current editor state. Uploads any pending data-URL assets,
 * then either updates the existing row (when `existingId` is set) or creates
 * a new project. Updates the live name + id stores and the saved-snapshot.
 */
export const saveProject = async ({
  dispatch,
  deviceConfig,
  existingId,
  name,
  forkedFromSlug,
}: SaveProjectArgs): Promise<SaveProjectResult> => {
  const state = store.getState().present

  // Upload pending background images (data URLs not yet on S3)
  await Promise.all(
    state.scenes.scenes.map(async (scene, idx) => {
      if (!scene.bgImageUrl || scene.bgImageKey || !scene.bgImageUrl.startsWith('data:')) return
      const res = await fetch(scene.bgImageUrl)
      const blob = await res.blob()
      const ext = blob.type.split('/')[1] ?? 'png'
      const file = new File([blob], `background.${ext}`, {type: blob.type})
      const {key, url} = await uploadAsset(file)
      dispatch(setBgImageUploaded({index: idx, key, url}))
    }),
  )

  // Upload pending screen images (data URLs not yet on S3)
  await Promise.all(
    state.scenes.scenes.map(async (scene, idx) => {
      if (!scene.screenImageUrl || scene.screenImageKey || !scene.screenImageUrl.startsWith('data:')) return
      const res = await fetch(scene.screenImageUrl)
      const blob = await res.blob()
      const ext = blob.type.split('/')[1] ?? 'png'
      const file = new File([blob], `screen.${ext}`, {type: blob.type})
      const {key, url} = await uploadAsset(file, 'screenshot')
      dispatch(setScreenImageUploaded({index: idx, key, url}))
    }),
  )

  // Upload pending custom-decal images (data URLs not yet on S3)
  await Promise.all(
    state.scenes.scenes.flatMap((scene, idx) =>
      scene.customDecals
        .filter((decal) => decal.imageUrl && !decal.imageKey && decal.imageUrl.startsWith('data:'))
        .map(async (decal) => {
          const res = await fetch(decal.imageUrl)
          const blob = await res.blob()
          const ext = blob.type.split('/')[1] ?? 'png'
          const file = new File([blob], `decal.${ext}`, {type: blob.type})
          const {key, url} = await uploadAsset(file, 'decal')
          dispatch(setCustomDecalUploaded({sceneIndex: idx, id: decal.id, key, url}))
        }),
    ),
  )

  // Upload pending 2D-sprite images (data URLs not yet on S3 — e.g. created anonymously)
  await Promise.all(
    state.scenes.scenes.flatMap((scene, idx) =>
      scene.sprites2d
        .filter((sprite) => sprite.imageUrl && !sprite.imageKey && sprite.imageUrl.startsWith('data:'))
        .map(async (sprite) => {
          const res = await fetch(sprite.imageUrl)
          const blob = await res.blob()
          const ext = blob.type.split('/')[1] ?? 'png'
          const file = new File([blob], `sprite.${ext}`, {type: blob.type})
          const {key, url} = await uploadAsset(file)
          dispatch(setSprite2DUploaded({sceneIndex: idx, id: sprite.id, key, url}))
        }),
    ),
  )

  // Upload pending 3D-sprite images (data URLs not yet on S3)
  await Promise.all(
    state.scenes.scenes.flatMap((scene, idx) =>
      scene.sprites3d
        .filter((sprite) => sprite.imageUrl && !sprite.imageKey && sprite.imageUrl.startsWith('data:'))
        .map(async (sprite) => {
          const res = await fetch(sprite.imageUrl)
          const blob = await res.blob()
          const ext = blob.type.split('/')[1] ?? 'png'
          const file = new File([blob], `sprite.${ext}`, {type: blob.type})
          const {key, url} = await uploadAsset(file)
          dispatch(setSprite3DUploaded({sceneIndex: idx, id: sprite.id, key, url}))
        }),
    ),
  )

  // Re-read state after uploads so serialized config includes S3 keys
  const updatedState = store.getState().present
  const merged = serializeConfig(updatedState, deviceConfig)
  const names = updatedState.scenes.sceneNames
  const scenesPayload = merged.scenes.map((s, idx) => ({
    name: names[idx] ?? `Scene ${idx + 1}`,
    config: s as unknown as Record<string, unknown>,
  }))

  let savedId: string
  if (existingId && existingId !== 'new') {
    await updateMyProject(existingId, {scenes: scenesPayload})
    savedId = existingId
  } else if (inFlightCreate) {
    // Another save is already creating this project — reuse its id and persist as update.
    savedId = await inFlightCreate
    await updateMyProject(savedId, {scenes: scenesPayload})
  } else {
    const projectNameValue = name ?? 'Untitled Project'
    inFlightCreate = createMyProject({
      name: projectNameValue,
      scenes: scenesPayload,
      ...(forkedFromSlug && {forkedFromSlug}),
    }).then((res) => res.data.id)
    try {
      savedId = await inFlightCreate
    } finally {
      inFlightCreate = null
    }
    setGlobalProjectName(projectNameValue)
    setGlobalProjectId(savedId)
    window.history.replaceState(null, '', `/editor/${savedId}`)
  }

  // Capture snapshot after pending dispatches settle so leave guard sees clean state.
  await new Promise((r) => requestAnimationFrame(r))
  const finalState = store.getState().present
  setSavedSnapshot(stableStringify(serializeConfig(finalState, deviceConfig)))

  return {id: savedId}
}
